import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMenuOpen(prev => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Close menu when clicking outside or on route change
  useEffect(() => {
    if (isMenuOpen) {
      const handleClickOutside = (e: Event) => {
        const target = e.target as HTMLElement;
        // Don't close if clicking on toggle button or inside navigation
        if (
          target.closest('.mobile-menu-toggle') ||
          target.closest('.main-nav')
        ) {
          return;
        }
        // Close if clicking on backdrop or outside
        setIsMenuOpen(false);
      };
      // Use a small delay to avoid immediate closure when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    closeMenu();
    
    const isOnHomePage = location.pathname === '/';
    
    if (isOnHomePage) {
      // If on home page, just scroll to section
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on home page, navigate to home first, then scroll after navigation
      navigate('/', { state: { scrollTo: hash } });
    }
  };

  // Handle scrolling after navigation
  useEffect(() => {
    if (location.state && (location.state as any).scrollTo) {
      const hash = (location.state as any).scrollTo;
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      // Clear the state to prevent scrolling on every render
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  return (
    <header className="professional-header">
      <div className="header-container">
        <Link to="/" className="logo-section" onClick={closeMenu}>
          <div className="logo">
            <img src="/images/logo.png" alt="Basant Kumar Verma Logo" className="logo-image" />
          </div>
          <span className="brand-name">Basant Kumar Verma</span>
        </Link>
        <div className="header-actions">
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <button 
            className="mobile-menu-toggle" 
            onClick={toggleMenu} 
            type="button"
            aria-label="Toggle menu" 
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {isMenuOpen && <div className="mobile-menu-backdrop" onClick={closeMenu}></div>}
        <nav className={`main-nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <a href="#about" className="nav-link" onClick={(e) => handleNavClick(e, '#about')}>About</a>
          <a href="#career" className="nav-link" onClick={(e) => handleNavClick(e, '#career')}>IT Career</a>
          <a href="#testimonials" className="nav-link" onClick={(e) => handleNavClick(e, '#testimonials')}>Testimonials</a>
          <a href="#content" className="nav-link" onClick={(e) => handleNavClick(e, '#content')}>Content</a>
          <a href="#contact" className="nav-link" onClick={(e) => handleNavClick(e, '#contact')}>Contact</a>
        </nav>
      </div>
    </header>
  );
}

export default Header;
